import { _ } from '@zto/zpage'

import type { VueComponent, AppPageDefinition, AppEnvMap, App } from '@zto/zpage'
import type { GlobMap } from '../typings/app'

import { COOKIE_CODES } from '../consts'
import { cookie } from './cookie'

// 认证数据缓存
const __auth_cache = new Map()

/** 应用认证数据 */
export interface IAppAuthData {
  deptCode?: string | null
  iamToken?: string | null
  portalRoleCode?: string | null
  positionCode?: string | null
}

/**
 * 获取应用认证信息
 * 从缓存中获取，防止用户登录多个客户端，并切换状态，导致获取错误的用户数据
 */
export function getAppAuthData(fromCookie?: boolean): IAppAuthData {
  const getFn = key => {
    if (fromCookie) {
      return cookie.get(key)
    }

    return __auth_cache.get(key)
  }

  const iamToken = getFn(COOKIE_CODES.IAM_TOKEN)
  const portalRoleCode = getFn(COOKIE_CODES.PORTAL_ROLE_CODE)
  const deptCode = getFn(COOKIE_CODES.DEPT_CODE)
  const positionCode = getFn(COOKIE_CODES.USER_POSITION_CODE)

  return { deptCode, iamToken, portalRoleCode, positionCode }
}

/** 应用认证信息 */
export function clearAppAuthData() {
  cookie.remove(COOKIE_CODES.DEPT_CODE)
  cookie.remove(COOKIE_CODES.IAM_TOKEN)
  cookie.remove(COOKIE_CODES.PORTAL_ROLE_CODE)
  cookie.remove(COOKIE_CODES.USER_POSITION_CODE)

  __auth_cache.clear()

  cookie.remove(COOKIE_CODES.LINK_ID)
  localStorage.removeItem(COOKIE_CODES.LINK_ID)
}

/** 应用认证信息 */
export function saveAppAuthData(data: IAppAuthData) {
  if (!data) return

  // 设置authData
  const setFn = (key, val) => {
    __auth_cache.set(key, val)
    cookie.set(key, val)
  }

  if (data.deptCode) setFn(COOKIE_CODES.DEPT_CODE, data.deptCode)
  if (data.iamToken) setFn(COOKIE_CODES.IAM_TOKEN, data.iamToken)
  if (data.portalRoleCode) setFn(COOKIE_CODES.PORTAL_ROLE_CODE, data.portalRoleCode)
  if (data.positionCode) setFn(COOKIE_CODES.USER_POSITION_CODE, data.positionCode)
}

/** 根据多个环境变量Map获取环境变量 */
export function getCurrentEnvFromAppEnvMaps<T>(envMaps: Record<string, AppEnvMap<T>>) {
  let env: any = null

  Object.entries(envMaps).forEach(([appName, envMap]) => {
    if (env) return

    let _env = getCurrentEnvFromEnvMap(envMap, false)

    if (_env) env = { appName, ..._env }
  })

  return env
}

/** 获取当前环境变量 */
export function getCurrentEnvFromEnvMap<T>(envMap: AppEnvMap<T>, defaultValue = true): T | null {
  const envNames = Object.keys(envMap)

  let envName = envNames.find(key => {
    const _env = envMap[key]
    return _env && _env.HOSTs && _env.HOSTs.some(h => checkHrefHost(h))
  })

  if (!envName) {
    if (defaultValue === false) return null
    envName = envNames[0] // 默认环境
  }

  const env = (envMap[envName] || {}).ENV

  return { name: envName, ...env } as unknown as T
}

/** 检查当前href是否为指定的host */
export function checkHrefHost(host: string) {
  const href = window.location.href
  const hostIndex = href.indexOf(host)

  if (hostIndex === 0 || ['https://', 'http://'].includes(href.substring(0, hostIndex))) return true

  return false
}

/** 根据Glob Map获取扩展对象数组 */
export function getComponentsFromGlobMap<T = VueComponent>(itemsMap: Record<string, any>): T[] {
  const items: T[] = _.objectToArray(itemsMap, (it: any, key: string) => {
    const cmpt = it.default
    cmpt.name = cmpt.name || key.substring(key.lastIndexOf('/') + 1, key.lastIndexOf('.'))

    return cmpt
  })

  return items
}

/** 根据Glob Map获取扩展对象数组 */
export function getComponentsFromMap<T = VueComponent>(itemsMap: Record<string, any>): T[] {
  const items: T[] = _.objectToArray(itemsMap, (it: any, key: string) => {
    const cmpt = it.default || it
    cmpt.name = cmpt.name || key

    return cmpt
  })

  return items
}

/** 获取组件记录 */
export function getComponentsRecords(components: VueComponent[]) {
  return components.reduce((acc, cmpt) => {
    acc[cmpt.name as string] = cmpt
    return acc
  }, {} as Record<string, VueComponent>)
}

export interface GetPagesFromGlobMapOptions {
  basePath?: string
  indexPostfix?: string
}

/**
 * 由页面的Glob Map获取页面数组
 * @param pagesMap
 * @param options
 */
export function getPagesFromGlobMap(
  pagesMap: GlobMap<AppPageDefinition>,
  options: GetPagesFromGlobMapOptions = {}
): AppPageDefinition[] {
  options = { basePath: '../pages', indexPostfix: '/index.ts', ...options }

  const indexFile = `${options.basePath}${options.indexPostfix}`

  // 预处理导出页面
  const pages = _.objectToArray(pagesMap, (item, key: string) => {
    // index文件不导出
    if (key === indexFile) return undefined

    const page = item.default || item.page
    if (!page) return undefined

    if (!page.path) page.path = _getPagePathByPageKey(key, options as any)
    return page
  }).filter((page: any) => !!page)

  return pages as AppPageDefinition[]
}

function _getPagePathByPageKey(key: string, options: Required<GetPagesFromGlobMapOptions>) {
  if (!key) return key

  const { basePath, indexPostfix } = options

  let path = key.substring(basePath.length)

  if (path.endsWith(indexPostfix)) {
    path = path.substring(0, path.length - indexPostfix.length)
  } else if (path.endsWith('.ts')) {
    path = path.substring(0, path.length - '.ts'.length)
  }

  return path
}
